// Canvas-drawn map icons.
//
// Generated at runtime rather than shipped as a sprite sheet: five shapes do
// not justify an asset pipeline, and this keeps the build free of binary files
// and the page free of extra requests.
//
// Drawn at 96px and registered at pixelRatio 2, giving a 48px logical base.
// The earlier 48px source was too small to stay crisp once scaled up, so the
// glyphs went soft exactly when they were meant to be easiest to see.

import { DAQI_COLOURS, airIconName } from './air-colours.ts';
import { FESTIVAL_COLOURS, festivalIconName } from './festival-colours.ts';
import { CLUSTERED_ICONS, clusterSpriteFor } from './cluster.ts';
import { COUNT_PLATE, ROUTE_ARROW } from './map-style.ts';

export type IconName =
  | 'plane' | 'helicopter'
  | 'cargo' | 'tanker' | 'passenger' | 'fishing' | 'ship' | 'ferry'
  | 'bus'
  | 'fuel'
  | 'rig' | 'buoy' | 'power'
  | 'air-station'
  // air-1 … air-10, one per Daily Air Quality Index value — see airIndexRender.
  | `air-${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10}`
  | 'river' | 'river-rising' | 'river-falling' | 'river-steady'
  | 'tide-rising' | 'tide-falling' | 'tide-flat'
  | 'taxi'
  | 'camera'
  | 'tram-stop'
  | 'subway-stop'
  | 'fringe'
  | 'roadworks' | 'roadworks-future' | 'incident' | 'incident-bridge' | 'road-closed' | 'vms'
  // The cluster marks — the member's own sprite, on a plate for the count.
  // One per entry in CLUSTERED_ICONS; buildIcons refuses to start without a
  // drawing for each, so this list and that one cannot drift apart in silence.
  | `${'roadworks' | 'roadworks-future'}-cluster`
  | `cso-${'not-discharging' | 'recent' | 'discharging' | 'no-data'}-cluster`
  | 'journey' | 'journey-slow' | 'journey-stopped'
  | 'cso-not-discharging' | 'cso-recent' | 'cso-discharging' | 'cso-no-data'
  | 'route-arrow'
  // train1 … train8, one per carriage count — see trainPathFor.
  | `train${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}`;

export { MAX_DRAWN_CARRIAGES };

const SIZE = 96;

/** Shapes are drawn in a ±34 box, leaving room for the halo inside the canvas. */
function draw(
  path: (ctx: CanvasRenderingContext2D) => void,
  fill: string,
  overlay?: (ctx: CanvasRenderingContext2D) => void,
): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;

  ctx.translate(SIZE / 2, SIZE / 2);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  // Dark halo first, so the icon reads against pale streets and dark water
  // alike. Without it these vanish over a light basemap.
  ctx.shadowColor = 'rgba(0, 0, 0, 0.65)';
  ctx.shadowBlur = 7;
  ctx.strokeStyle = '#0b1016';
  ctx.lineWidth = 7;
  ctx.beginPath();
  path(ctx);
  ctx.closePath();
  ctx.stroke();

  // Body, with a light rim for definition against the halo.
  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  ctx.beginPath();
  path(ctx);
  ctx.closePath();
  ctx.fill();

  ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Deck detail sits inside the finished hull, so it needs no halo of its own.
  if (overlay) {
    ctx.save();
    overlay(ctx);
    ctx.restore();
  }

  return ctx.getImageData(0, 0, SIZE, SIZE);
}

/** Nose-up arrow: MapLibre rotates it by the vehicle's heading. */
function planePath(ctx: CanvasRenderingContext2D): void {
  ctx.moveTo(0, -34);
  ctx.lineTo(8, -12);
  ctx.lineTo(34, 8);
  ctx.lineTo(34, 16);
  ctx.lineTo(8, 8);
  ctx.lineTo(6, 26);
  ctx.lineTo(16, 34);
  ctx.lineTo(0, 30);
  ctx.lineTo(-16, 34);
  ctx.lineTo(-6, 26);
  ctx.lineTo(-8, 8);
  ctx.lineTo(-34, 16);
  ctx.lineTo(-34, 8);
  ctx.lineTo(-8, -12);
}

/**
 * Top-down helicopter: rotor disc, crossed main-rotor blades, capsule fuselage,
 * tail boom and tail rotor.
 *
 * Replaces an outline that collapsed into a flat yellow blob at map sizes. The
 * proportions are ported from the SVG in the design handoff, scaled from its
 * 76px viewBox to this 96px canvas (×1.26) and re-centred on the fuselage.
 *
 * This one needs strokes as well as a fill, so it is drawn by drawComposite()
 * rather than the single-path draw() the other icons use.
 */
function helicopterRender(ctx: CanvasRenderingContext2D, fill: string, outline: string): void {
  const capsulePath = (x: number, y: number, w: number, h: number, r: number) => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
  };

  /**
   * The whole silhouette, drawn twice: once wide in the dark outline as a halo,
   * once in the body colour on top.
   *
   * The design mockup draws the rotor in near-black, which is right on a white
   * card and wrong on a map — dark strokes vanish over a dark basemap, leaving
   * a yellow speck. Same halo-then-body pipeline as every other icon here.
   */
  const geometry = (widen: number) => {
    // Rotor disc, translucent so it reads as motion rather than structure.
    ctx.globalAlpha = widen ? 1 : 0.55;
    ctx.lineWidth = 3 + widen;
    ctx.beginPath();
    ctx.arc(0, -10, 25, 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;

    // Crossed main-rotor blades.
    ctx.lineWidth = 4.5 + widen;
    ctx.beginPath();
    ctx.moveTo(-25, -10); ctx.lineTo(25, -10);
    ctx.moveTo(0, -35); ctx.lineTo(0, 15);
    ctx.stroke();

    // Tail rotor bar.
    ctx.lineWidth = 3.5 + widen;
    ctx.beginPath();
    ctx.moveTo(-10, 33); ctx.lineTo(10, 33);
    ctx.stroke();

    // Tail boom first, so the fuselage overlaps its top edge cleanly.
    ctx.lineWidth = 3 + widen;
    capsulePath(-4, 15, 8, 20, 4);
    ctx.fill();
    ctx.stroke();
    capsulePath(-10, -15, 20, 33, 10);
    ctx.fill();
    ctx.stroke();
  };

  ctx.strokeStyle = outline;
  ctx.fillStyle = outline;
  geometry(5);

  // Body pass sits inside the halo; no shadow, or it muddies the fill.
  ctx.shadowBlur = 0;
  ctx.strokeStyle = fill;
  ctx.fillStyle = fill;
  geometry(0);

  // Dark canopy dot, so the nose direction is readable at a glance.
  ctx.fillStyle = outline;
  ctx.beginPath();
  ctx.arc(0, -6, 4.5, 0, Math.PI * 2);
  ctx.fill();
}

// ── Vessel silhouettes ──────────────────────────────────────────────────────
//
// Five top-down, nose-up hulls ported from the design handoff's SVG paths.
// Each viewBox is normalised so every hull spans the same ±38 of this canvas:
// SHAPE encodes the vessel type, SIZE comes from real hull length via the
// icon-size expression in map.ts. Baking the mockup's differing viewBox heights
// in as well would double-count size and draw a small cargo boat like a 200 m
// ship.

function cargoPath(ctx: CanvasRenderingContext2D): void {
  ctx.moveTo(0, -35);
  ctx.lineTo(11.1, -24.8);
  ctx.lineTo(11.1, 27.9);
  ctx.lineTo(6.1, 35);
  ctx.lineTo(-6.1, 35);
  ctx.lineTo(-11.1, 27.9);
  ctx.lineTo(-11.1, -24.8);
}

/** Four deck-container rows and a light bridge block near the stern. */
function cargoOverlay(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = OUTLINE;
  for (const [y, alpha] of [[-21.8, 0.75], [-12.7, 0.6], [-3.6, 0.75], [5.6, 0.6]] as const) {
    ctx.globalAlpha = alpha;
    ctx.fillRect(-8.6, y, 17.2, 7.1);
  }
  ctx.globalAlpha = 1;
  ctx.fillStyle = '#e8eef5';
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  roundedRect(ctx, -7.1, 16.7, 14.2, 12.2, 1.5);
  ctx.fill();
  ctx.stroke();
}

/** Capsule hull, rounded at both ends — passenger ships and ferries. */
function passengerPath(ctx: CanvasRenderingContext2D): void {
  ctx.moveTo(0, -35);
  ctx.quadraticCurveTo(15.2, -25.8, 15.2, -15.2);
  ctx.lineTo(15.2, 19.8);
  ctx.quadraticCurveTo(15.2, 28.9, 0, 35);
  ctx.quadraticCurveTo(-15.2, 28.9, -15.2, 19.8);
  ctx.lineTo(-15.2, -15.2);
  ctx.quadraticCurveTo(-15.2, -25.8, 0, -35);
}

/** One full-beam superstructure block over the middle of the length. */
function passengerOverlay(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = '#e8eef5';
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 2;
  roundedRect(ctx, -10.6, -18.2, 21.3, 36.5, 4.6);
  ctx.fill();
  ctx.stroke();
}

/** Smooth tapered hull, rounded both ends, plain deck. */
function tankerPath(ctx: CanvasRenderingContext2D): void {
  ctx.moveTo(0, -36.2);
  ctx.quadraticCurveTo(11.9, -26.1, 11.9, -17.8);
  ctx.lineTo(11.9, 21.4);
  ctx.quadraticCurveTo(11.9, 30.9, 0, 36.2);
  ctx.quadraticCurveTo(-11.9, 30.9, -11.9, 21.4);
  ctx.lineTo(-11.9, -17.8);
  ctx.quadraticCurveTo(-11.9, -26.1, 0, -36.2);
}

/** A single dark block at the stern, and nothing else on deck. */
function tankerOverlay(ctx: CanvasRenderingContext2D): void {
  ctx.fillStyle = OUTLINE;
  ctx.globalAlpha = 0.7;
  roundedRect(ctx, -7.1, 19, 14.3, 9.5, 1.8);
  ctx.fill();
  ctx.globalAlpha = 1;
}

/** Plain small hull — tugs, pleasure craft, anything unclassified. */
function plainBoatPath(ctx: CanvasRenderingContext2D): void {
  ctx.moveTo(0, -34.8);
  ctx.lineTo(12.7, -9.5);
  ctx.lineTo(12.7, 19);
  ctx.lineTo(6.3, 31.7);
  ctx.lineTo(-6.3, 31.7);
  ctx.lineTo(-12.7, 19);
  ctx.lineTo(-12.7, -9.5);
}

/** Same hull family as the plain boat, distinguished only by the boom. */
function fishingPath(ctx: CanvasRenderingContext2D): void {
  ctx.moveTo(0, -34.2);
  ctx.lineTo(11.4, -11.4);
  ctx.lineTo(11.4, 19);
  ctx.lineTo(5.7, 30.4);
  ctx.lineTo(-5.7, 30.4);
  ctx.lineTo(-11.4, 19);
  ctx.lineTo(-11.4, -11.4);
}

function fishingOverlay(ctx: CanvasRenderingContext2D): void {
  ctx.strokeStyle = OUTLINE;
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-20.9, -7.6);
  ctx.lineTo(20.9, -7.6);
  ctx.stroke();
}

function roundedRect(
  ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

/** Rounded rectangle with a nose notch — buses have no useful silhouette. */
function busPath(ctx: CanvasRenderingContext2D): void {
  const w = 20;
  const h = 30;
  const r = 8;
  ctx.moveTo(-w + r, -h);
  ctx.arcTo(w, -h, w, -h + r, r);
  ctx.arcTo(w, h, w - r, h, r);
  ctx.arcTo(-w, h, -w, h - r, r);
  ctx.arcTo(-w, -h, -w + r, -h, r);
}

/**
 * A train drawn as its actual formation: one segment per carriage, nose first.
 *
 * The count is the point. Portlethen's platform takes two carriages, so on
 * anything longer only the front doors open and passengers have to walk
 * forward — which is exactly the sort of thing you want to see coming rather
 * than discover when the doors beside you stay shut. A generic train blob
 * cannot say that; four visible carriages can.
 *
 * Carriages are a fixed width and a fixed length each, so a 2-car unit really
 * is shorter than a 9-car set on screen. Beyond MAX_DRAWN they stop being
 * countable and the icon would outgrow the canvas, so longer formations draw
 * at that cap — the popup still gives the exact number.
 */
const MAX_DRAWN_CARRIAGES = 8;
const CARRIAGE_LENGTH = 15;
const CARRIAGE_GAP = 2.5;
const TRAIN_HALF_WIDTH = 11;

function trainPathFor(carriages: number) {
  const n = Math.max(1, Math.min(MAX_DRAWN_CARRIAGES, carriages));
  const total = n * CARRIAGE_LENGTH + (n - 1) * CARRIAGE_GAP;
  // Centred on the canvas so rotation pivots about the middle of the train,
  // not its nose.
  const top = -total / 2;

  return (ctx: CanvasRenderingContext2D): void => {
    for (let i = 0; i < n; i += 1) {
      const y = top + i * (CARRIAGE_LENGTH + CARRIAGE_GAP);
      const w = TRAIN_HALF_WIDTH;
      // The leading carriage gets a tapered nose so direction of travel reads
      // at a glance; the rest are plain rounded boxes.
      if (i === 0) {
        ctx.moveTo(0, y);
        ctx.lineTo(w, y + 7);
        ctx.lineTo(w, y + CARRIAGE_LENGTH);
        ctx.lineTo(-w, y + CARRIAGE_LENGTH);
        ctx.lineTo(-w, y + 7);
        ctx.closePath();
      } else {
        ctx.moveTo(-w, y);
        ctx.lineTo(w, y);
        ctx.lineTo(w, y + CARRIAGE_LENGTH);
        ctx.lineTo(-w, y + CARRIAGE_LENGTH);
        ctx.closePath();
      }
    }
  };
}

function trainOverlayFor(carriages: number) {
  const n = Math.max(1, Math.min(MAX_DRAWN_CARRIAGES, carriages));
  const total = n * CARRIAGE_LENGTH + (n - 1) * CARRIAGE_GAP;
  const top = -total / 2;

  return (ctx: CanvasRenderingContext2D): void => {
    ctx.fillStyle = 'rgba(11, 16, 22, 0.8)';
    for (let i = 0; i < n; i += 1) {
      const y = top + i * (CARRIAGE_LENGTH + CARRIAGE_GAP);
      // A window band per carriage. It reinforces the count at zoom levels
      // where the inter-carriage gaps alone start to close up.
      ctx.beginPath();
      ctx.roundRect(-6, y + (i === 0 ? 8 : 4), 12, CARRIAGE_LENGTH - (i === 0 ? 11 : 8), 2);
      ctx.fill();
    }
  };
}

export const PALETTE = {
  plane: '#4cc2ff',
  helicopter: '#ffd60a',
  ship: '#32d74b',
  ferry: '#ff9f0a',
  bus: '#ff6b6b',
  /** Violet: unused by any observed layer, so an estimate never reads as a fix. */
  train: '#bf5af2',
  /** Forecourts. Amber reads as a price tag rather than as an alert. */
  fuel: '#ffd60a',
  /**
   * Offshore installations. Steel grey rather than a signal colour: a rig is a
   * permanent fixture of this coast, not an event, and it sits on the map all
   * the time. Anything brighter would compete with the vehicles that move.
   */
  /*
   * Rigs. Brighter than the old grey now the marker has no plate behind it:
   * a bare silhouette carries its own contrast, and #b0bec5 was chosen to sit
   * against a dark outline rather than against open sea.
   */
  rig: '#e8eef5',
  /** Wave buoys. The same blue the sea and river layers already use. */
  buoy: '#4fc3f7',
  /**
   * The bolt drawn inside a power circle.
   *
   * Near-black rather than a colour, because the circle underneath already
   * carries the severity — red for customers off, amber for planned work, green
   * for a fault with nobody off. A coloured glyph would compete with the thing
   * it sits on; a dark one reads as a hole punched in it, at any severity.
   */
  power: '#0b1016',
  /** River gauges, drawn inside their circle like the bolt and for the same reason. */
  river: '#0b1016',
  /**
   * A monitor with nothing to say. The CSO marker's three real states reuse
   * the journey severities below — one severity language across every layer,
   * per the design handoff — but "no data" is not a severity claim, and green
   * would be a claim of fine the monitor has not made. The muted grey the UI
   * already uses for secondary text, so it reads as "muted", not "good" or
   * "bad".
   */
  csoNoData: '#8b9bad',
  /**
   * Air quality stations. Near-black like the river and power glyphs: the
   * disc behind it carries the layer's colour, and this layer never claims a
   * severity — the feed publishes concentrations, not the official band — so
   * a coloured glyph would be saying something the data does not.
   */
  air: '#0b1016',
  /** Tides. Same treatment, and deliberately the same drawing — see buildIcons. */
  tide: '#0b1016',
  /**
   * Taxi ranks. Near-black because a black cab is black; it stands on the
   * basemap with no disc behind it, so the white halo and cut-outs carry the
   * contrast, the way the rig's silhouette does.
   */
  taxi: '#0b1016',
  /**
   * Traffic cameras. Steel grey — the grey the weather glyph already uses —
   * and deliberately not amber: the roads layers spend amber on the cone and
   * the warning triangle, and a camera is not a hazard. A third amber
   * silhouette in that group is how a reader stops reading the group.
   */
  camera: '#9fb4c7',
  /**
   * Tram platforms. The purple the tram ring already wore on this map, and the
   * one the panel row draws in — the shape is what changed, not the colour, so
   * anyone who had learned "purple means tram" keeps that reading.
   *
   * Coloured rather than near-black like the cab, because this mark's whole job
   * is to be told apart from 1,481 grey bus circles at a glance. Black would
   * hand that work entirely to the silhouette, which is the part that goes
   * first when the sprite is ten pixels across.
   */
  tramStop: '#a468d6',
  /**
   * Subway stations. The orange Glasgow already calls the Clockwork Orange —
   * the one colour this mark could be, by the same argument that made the
   * platform sprite tram purple: the reading exists before the map does.
   * Unclaimed by any layer this city shows; the ferry amber below is the
   * nearest neighbour and lives in a different harbour.
   */
  subwayStop: '#ff8c1a',
  /**
   * Fringe venues. A colour of its own because the mark stands on the basemap
   * with no disc — festival pink, unclaimed by any severity or layer, and
   * unmistakable among three hundred Old Town pins at once.
   */
  fringe: '#ff5c8a',
  /**
   * Roadworks. Cone orange, because that is what a cone is — the colour is
   * doing the same work as the shape here, and recolouring it by severity
   * would fight a signal every driver already reads without thinking.
   */
  cone: '#ff7a1a',
  /** The red of a UK warning sign's border. */
  hazard: '#d8232a',
  /** A gantry sign's face. The amber is the lamp colour, added in the render. */
  vms: '#1b2430',
  /**
   * Journey times, moving normally.
   *
   * Green on purpose, and it is the one place on this map where green is a
   * claim rather than an absence: every other layer draws only when something
   * has happened, so silence means fine. A journey time is always there, so it
   * has to say "fine" out loud. The comment here used to describe a cool blue,
   * which is what this was before the three bands went in — a colour chosen to
   * keep a measurement out of the warning register altogether. Bands made that
   * argument moot: green *is* the not-a-warning, and amber and red below are
   * the warning when there is one.
   */
  journey: '#2f9e5f',
  /**
   * Slow, and stopped. Deliberately the amber and red already used by the cone
   * and the hazard sign, so one severity language runs across every roads
   * layer — a reader who has learned that red means "bad" on an incident does
   * not have to learn it again for a journey time.
   */
  journeySlow: '#f0a93b',
  journeyStopped: '#d8232a',
} as const;

/**
 * Same halo-then-body pipeline as draw(), but for icons made of several strokes
 * and fills rather than one closed path. The halo is faked with a shadow on the
 * whole composite so it still reads against a pale basemap.
 */
/**
 * A petrol pump, seen side-on.
 *
 * Drawn small and squat: it sits beside a price label, and an icon that
 * competes with the number defeats the point — on a fuel map the price is the
 * information and the pump is only there to say what kind of place this is.
 *
 * Composite rather than one path because the hose curls back on itself, which
 * a single closed path cannot express without a fill artefact.
 */
function fuelPumpRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  outline: string,
): void {
  // Pump body — a rounded upright box.
  const body = () => {
    ctx.beginPath();
    ctx.roundRect(-16, -20, 22, 40, 4);
  };

  ctx.strokeStyle = outline;
  ctx.lineWidth = 8;
  body();
  ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  body();
  ctx.fill();
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.lineWidth = 2;
  ctx.stroke();

  // Display window, so it reads as a pump and not a filing cabinet.
  ctx.fillStyle = outline;
  ctx.beginPath();
  ctx.roundRect(-12, -15, 14, 11, 2);
  ctx.fill();

  // Nozzle arm and hose, rising from the right of the body.
  ctx.strokeStyle = outline;
  ctx.lineWidth = 8;
  ctx.beginPath();
  ctx.moveTo(6, 6);
  ctx.lineTo(14, 6);
  ctx.lineTo(14, -12);
  ctx.quadraticCurveTo(14, -18, 8, -18);
  ctx.stroke();

  ctx.strokeStyle = fill;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(6, 6);
  ctx.lineTo(14, 6);
  ctx.lineTo(14, -12);
  ctx.quadraticCurveTo(14, -18, 8, -18);
  ctx.stroke();
}

/**
 * A taxi rank: a hackney carriage seen head on, with its lamp lit.
 *
 * Front rather than profile because a rank has no heading. A vehicle drawn in
 * profile reads as something travelling — as traffic, or as one of the vehicle
 * layers — and this layer marks a place where cabs wait.
 *
 * The FX4 face carries the recognition: narrower at the roof than at the
 * wings, a tall radiator grille between two round headlamps, and the sign on
 * top. Three cut-outs and nothing else, because at nineteen pixels a fourth
 * closes the gaps up into a grey blob — a checker band, the other obvious taxi
 * signal, does exactly that and is not drawn here.
 *
 * The lamp is painted amber, the one warm mark on the glyph and the same
 * device rigRender uses for its flare. A lit lamp means for hire, which is
 * what somebody standing in the rain is looking for, and it is also the
 * quickest way to tell this apart from the power bolt at map sizes.
 *
 * Dark body on a pale halo, like the bolt — but drawn straight on the basemap
 * with no disc behind it: a rank has no severity for a disc to carry, and a
 * black cab supplies its own colour.
 */
function taxiRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  _outline: string,
): void {
  // Body as one outline, so the halo traces the whole silhouette instead of
  // appearing between the parts.
  const body = () => {
    ctx.beginPath();
    ctx.moveTo(-16.5, -25);
    ctx.quadraticCurveTo(-20.5, -25, -21, -20);
    ctx.lineTo(-24, -3);
    ctx.lineTo(-26, 10);
    ctx.quadraticCurveTo(-26, 16, -20, 16);
    ctx.lineTo(20, 16);
    ctx.quadraticCurveTo(26, 16, 26, 10);
    ctx.lineTo(24, -3);
    ctx.lineTo(21, -20);
    ctx.quadraticCurveTo(20.5, -25, 16.5, -25);
    ctx.closePath();
  };

  // The sign, proud of the roof. Its base runs below the roofline on purpose:
  // butting the two shapes exactly at y=-25 leaves each with its own halo
  // stroke, and the two halos read as a white gap with the sign floating above
  // the car. Overlapping merges the fills into one silhouette.
  const sign = () => {
    ctx.beginPath();
    ctx.roundRect(-10, -33, 20, 10, 2);
  };

  ctx.lineJoin = 'round';

  // Halo first, under everything, so the shape survives on a coloured disc.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.lineWidth = 9;
  body(); ctx.stroke();
  sign(); ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  body(); ctx.fill();
  sign(); ctx.fill();

  // Screen, grille and headlamps knocked back out in the halo colour rather
  // than in white, so they read as part of the body at 19 px instead of as
  // separate marks.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.beginPath();
  ctx.moveTo(-15, -20);
  ctx.lineTo(15, -20);
  ctx.lineTo(17.5, -5.5);
  ctx.lineTo(-17.5, -5.5);
  ctx.closePath();
  ctx.fill();

  ctx.beginPath();
  ctx.roundRect(-6.5, 0.5, 13, 11, 2);
  ctx.fill();

  ctx.beginPath();
  ctx.arc(-17.5, 6, 5, 0, Math.PI * 2);
  ctx.moveTo(22.5, 6);
  ctx.arc(17.5, 6, 5, 0, Math.PI * 2);
  ctx.fill();

  // The lamp, lit. Drawn last so it sits inside the finished sign.
  ctx.fillStyle = '#F0961F';
  ctx.beginPath();
  ctx.roundRect(-7.4, -31, 14.8, 6, 1.6);
  ctx.fill();
}

/**
 * A traffic camera: a housing on a pole, tilted the way the real ones sit.
 *
 * From the camera design handoff, carrying the handoff's own 24×24 coordinates
 * across through a mapper — the tram platform's treatment — so the drawing can
 * be checked against the spec line by line. The interior detail is cut out in
 * #161f2b as the spec draws it: against the mid-grey body the panel dark reads
 * as a lens and a vent, where a white knockout would read as sky through holes.
 *
 * Pale halo and no disc, like the cab and the star: a camera is an object, not
 * an event, and has no severity for a circle to carry. The grey housing needs
 * the halo more than most — without it, it disappears over pale OpenFreeMap
 * streets. Stale cameras keep this same drawing and fade to half opacity in
 * the layer's paint instead; a second sprite would be a second thing to drift.
 */
function cameraRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  _outline: string,
): void {
  const S = 68 / 24;
  const g = (v: number) => (v - 12) * S;

  const pole = () => {
    ctx.beginPath();
    ctx.roundRect(g(11), g(12), 2.2 * S, 9.4 * S, 1.1 * S);
  };
  const base = () => {
    ctx.beginPath();
    ctx.roundRect(g(6), g(18.6), 12 * S, 2.6 * S, 1.3 * S);
  };
  const housing = () => {
    ctx.beginPath();
    ctx.roundRect(g(4.6), g(5.6), 11.6 * S, 6.4 * S, 1.8 * S);
  };
  const hood = () => {
    ctx.beginPath();
    ctx.roundRect(g(16.2), g(7.2), 3.1 * S, 3.2 * S, 1 * S);
  };

  // The handoff rotates the housing group -12° about (12, 9) in its own
  // units; paths built inside `tilted` inherit the transform.
  const tilted = (paint: () => void) => {
    ctx.save();
    ctx.translate(g(12), g(9));
    ctx.rotate((-12 * Math.PI) / 180);
    ctx.translate(-g(12), -g(9));
    paint();
    ctx.restore();
  };

  ctx.lineJoin = 'round';

  // Halo first, under everything — 3.4 units at the 24-unit scale, which is
  // the width the handoff names for the map sprite.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.lineWidth = 3.4 * S;
  pole(); ctx.stroke();
  base(); ctx.stroke();
  tilted(() => {
    housing(); ctx.stroke();
    hood(); ctx.stroke();
  });

  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  pole(); ctx.fill();
  base(); ctx.fill();
  tilted(() => {
    housing(); ctx.fill();
    hood(); ctx.fill();
  });

  // Vent and lens, cut out in the panel dark, inside the finished housing.
  ctx.fillStyle = '#161f2b';
  tilted(() => {
    ctx.beginPath();
    ctx.roundRect(g(6.4), g(7.4), 5.6 * S, 2.6 * S, 0.6 * S);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(g(17.7), g(8.8), 1.1 * S, 0, Math.PI * 2);
    ctx.fill();
  });
}

/**
 * A tram platform: a tram standing at a shelter, under the overhead wire.
 *
 * Ported from the supplied tramStops.svg, the same drawing the panel row uses,
 * so the row and the mark on the map are one object — the treatment the taxi
 * already gets, and the reason a reader who ticks a row recognises what appears.
 * The mapper carries the source's own 24×24 coordinates across rather than
 * hand-converted numbers, so this can be checked against the SVG line by line.
 *
 * ── Why a sprite at all ────────────────────────────────────────────────
 *
 * Tram platforms were a bus circle with a purple ring. That is a colour
 * difference and nothing else, and at the radius those circles are drawn — 3 px
 * at zoom 14 — a purple ring against a grey one is a distinction nobody can
 * make while moving a map about. There are 44 of them against Edinburgh's
 * thousands of bus stops, which is few enough to afford a real shape each, the
 * same trade the 18 taxi ranks got.
 *
 * ── The three departures from the source SVG ───────────────────────────
 *
 * 1. **Cut-outs are the halo colour, not #161f2b.** In the panel the window and
 *    wheels are knocked out in the panel's own background, which is why they
 *    read as detail rather than as holes. There is no panel behind a marker;
 *    the basemap is whatever it happens to be. So they are knocked out in the
 *    halo white, exactly as taxiRender does, and for the same reason.
 * 2. **Wire and pantograph thickened** from the source's 1.5 units to 2.2. At
 *    region zoom the drawn wire is under a pixel at 1.5 and the halo simply
 *    swallows it, leaving a tram apparently parked beside a lamp post — the
 *    same correction rigRender made to its legs.
 * 3. **Wire drawn as a filled bar rather than a stroke**, so the whole glyph is
 *    one path and takes one halo pass. The wire, its pantograph and the tram
 *    body deliberately overlap into a single silhouette; the shelter is left
 *    separate, so the halo shows daylight between the canopy and the wire above
 *    it. That white gap is wanted here — unlike the taxi's roof sign, where the
 *    two shapes are attached and a gap read as the sign floating.
 */
function tramStopRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  outline: string,
): void {
  const S = 68 / 24;
  // 0.92 rather than the rig's 0.95: this glyph runs the full 20 units of the
  // viewBox in x, and at 0.95 the halo on the wire's ends clips the canvas.
  const G = S * 0.92;
  // One mapper for both axes. The drawing's own centre — the wire's top edge at
  // 2.5 to the foot of the shelter post at 21.6 — lands within a twentieth of a
  // unit of the viewBox's 12, so there is nothing to correct for.
  const g = (v: number) => (v - 12) * G;

  /** Wire, pantograph, tram body, shelter post and canopy — one path, one halo. */
  const body = () => {
    ctx.beginPath();
    // The wire: the source's `M2 3.6h20` stroked at 2.2, as the bar it draws.
    ctx.roundRect(g(2), g(2.5), 20 * G, 2.2 * G, 1.1 * G);
    // The pantograph, overlapping the wire above and the roof below so the
    // three fills merge instead of each collecting its own outline.
    ctx.rect(g(7), g(3.6), 2 * G, 3.2 * G);
    ctx.roundRect(g(3), g(6.6), 10 * G, 10.4 * G, 2.2 * G);
    ctx.roundRect(g(15.4), g(6.6), 2.4 * G, 15 * G, 1.2 * G);
    ctx.roundRect(g(15.4), g(6.6), 6.6 * G, 2.6 * G, 1.3 * G);
  };

  ctx.lineJoin = 'round';

  // Halo, then body — the pipeline every sprite here uses. 8 rather than 9,
  // like the rig: the wire and the shelter post are thin members, and a heavier
  // outline closes the daylight between them into a blob.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.lineWidth = 8;
  body(); ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  body(); ctx.fill();

  // A dark rim on the purple, which the white-haloed sprites otherwise lack:
  // the cab and the star are near-black and pink, both of which hold their own
  // edge against white. #a468d6 against a 0.92 white halo does not, and without
  // this the glyph looks bleached at the zooms where it is smallest.
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.6;
  body(); ctx.stroke();

  // Window and wheels, knocked back out in the halo colour. Detail for the
  // zooms that can hold it: the wheels are two pixels across at region zoom and
  // simply are not there, which is the right outcome — the silhouette is what
  // has to survive that far out.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.beginPath();
  ctx.roundRect(g(5), g(8.8), 6 * G, 3.2 * G, 0.6 * G);
  ctx.fill();

  ctx.beginPath();
  ctx.arc(g(6.1), g(15), 0.85 * G, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(g(9.9), g(15), 0.85 * G, 0, Math.PI * 2);
  ctx.fill();
}

/**
 * A Subway station: an orange disc with a tunnel mouth knocked out of it.
 *
 * Fifteen of these on the Glasgow map, against thousands of bus circles — the
 * same count argument the trams and taxis won. The disc is the strongest shape
 * that survives ten pixels, and the arch inside it is the one line that says
 * "underground" without borrowing anybody's roundel. No lettering: at the
 * zooms where the mark matters a letter is a smudge, and the colour is doing
 * the naming — see PALETTE.subwayStop.
 *
 * The mouth stays inside the disc rather than breaking its rim, because the
 * knockout is painted in halo white the way the tram's windows are, and a
 * cut that crossed the edge would spill white onto the basemap as a tab.
 */
function subwayStopRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  outline: string,
): void {
  const disc = () => {
    ctx.beginPath();
    ctx.arc(0, 0, 30, 0, Math.PI * 2);
  };

  ctx.lineJoin = 'round';

  // Halo, fill, rim — the tram platform's pipeline, disc-shaped.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.lineWidth = 8;
  disc(); ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  disc(); ctx.fill();

  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.6;
  disc(); ctx.stroke();

  // The tunnel mouth: an arch, flat-bottomed, knocked out in the halo white.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.beginPath();
  ctx.roundRect(-13, -10, 26, 32, [13, 13, 0, 0]);
  ctx.fill();
}


/**
 * A Fringe venue: a five-pointed star.
 *
 * A star, not theatre masks or a tent — at nineteen pixels those collapse
 * into blobs, and a star survives being small the way the cone and the bolt
 * do. It replaces its circle outright, like the rig: a venue is a place with
 * no severity to encode, and during August there are hundreds of these in a
 * square mile, so the mark must be cheap to read in bulk.
 */
function fringeRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  _outline: string,
): void {
  const star = () => {
    ctx.beginPath();
    for (let i = 0; i < 10; i += 1) {
      const r = i % 2 === 0 ? 30 : 12.5;
      const a = (Math.PI / 5) * i - Math.PI / 2;
      const x = Math.cos(a) * r;
      const y = Math.sin(a) * r;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
    ctx.closePath();
  };

  ctx.lineJoin = 'round';

  // Halo first, so the shape survives whatever the basemap is doing.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.lineWidth = 9;
  star(); ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  star(); ctx.fill();
}

/**
 * A traffic cone, optionally with a clock in front of it.
 *
 * Square-ish in silhouette and unmistakable, which is what separates it from
 * the three circular layers — power cuts, river gauges and tides.
 * Shape is read faster than colour and survives being small.
 *
 * No coloured plate behind it, and so no severity in the mark. That is a
 * deliberate trade: a cone is orange, every driver knows what an orange cone
 * means, and tinting it red or blue to encode severity would fight a signal
 * people already read without thinking. Severity is still on the card and
 * still drives the Now box, which is where someone goes to find out how bad
 * it is — the map's job here is to say "roadworks, there".
 *
 * ── The clock ──────────────────────────────────────────────────────────────
 *
 * Planned works are the same object at a different time, so they get the same
 * cone with a clock leaning against it rather than a second silhouette. A
 * reader who has learned "cone means roadworks" keeps that, and the clock adds
 * the one thing this layer means differently: not now, later.
 *
 * The clock sits half off the cone rather than centred on it. Centred, it
 * covers both reflective bands and the mark stops reading as a cone at map
 * sizes — which would lose the very thing the two layers are meant to share.
 */
function coneRender(withClock: boolean) {
  return (ctx: CanvasRenderingContext2D, fill: string, _outline: string): void => {
    const body = () => {
      ctx.beginPath();
      ctx.moveTo(-5, -30);
      ctx.lineTo(5, -30);
      ctx.lineTo(19, 20);
      ctx.lineTo(-19, 20);
      ctx.closePath();
    };
    const base = () => {
      ctx.beginPath();
      ctx.roundRect(-27, 19, 54, 9, 4);
    };

    ctx.lineJoin = 'round';

    // Pale halo under the whole silhouette, so the cone reads on a dark basemap
    // and on a pale one alike — the same treatment every sprite here gets.
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
    ctx.lineWidth = 8;
    body(); ctx.stroke();
    base(); ctx.stroke();

    ctx.shadowBlur = 0;
    ctx.fillStyle = fill;
    body(); ctx.fill();
    base(); ctx.fill();

    // Two reflective bands, knocked out in the halo colour rather than in white:
    // they must read as part of the cone at 22 px, not as separate marks.
    ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
    ctx.beginPath();
    ctx.moveTo(-8.6, -12); ctx.lineTo(8.6, -12);
    ctx.lineTo(10.4, -5); ctx.lineTo(-10.4, -5);
    ctx.closePath();
    ctx.moveTo(-13.4, 2); ctx.lineTo(13.4, 2);
    ctx.lineTo(15.2, 9); ctx.lineTo(-15.2, 9);
    ctx.closePath();
    ctx.fill();

    if (!withClock) return;

    // Drawn last, so it is genuinely in front. Bottom right, where it overlaps
    // the widest part of the cone and its base — the reading is "clock stood
    // against a cone", not "clock and cone side by side".
    // Half the cone's base width. Smaller was tried and the dial collapsed to
    // a speck at city zoom, where the two cones have to be tellable apart.
    const cx = 14.5;
    const cy = 12.5;
    const r = 14.5;
    const dial = () => {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
    };

    // Dark ring rather than the pale halo the rest of the sprite uses: a white
    // dial on top of a white reflective band needs the separation to be the
    // opposite colour or the two merge into one blob.
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 9;
    dial(); ctx.stroke();

    ctx.fillStyle = '#ffffff';
    dial(); ctx.fill();

    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 3.8;
    dial(); ctx.stroke();

    // Hands at three o'clock — one up, one right. A right angle is what a clock
    // face reduces to when it is 14 px across, and it survives the blur that
    // any more naturalistic pose does not.
    ctx.lineCap = 'round';
    ctx.lineWidth = 3.8;
    ctx.beginPath();
    ctx.moveTo(cx, cy); ctx.lineTo(cx, cy - 8);
    ctx.moveTo(cx, cy); ctx.lineTo(cx + 7, cy);
    ctx.stroke();
  };
}

/**
 * Any mark, standing on a blank plate for a count.
 *
 * Drawn for the clusters (#60), which replaced a coloured disc with a number
 * in it. A disc says "several of something"; the member's own sprite says
 * "several roadworks" or "several overflowing outfalls", to a reader who has
 * already learned what those look like and without a legend — which is the
 * whole reason coneRender and csoOverflowRender exist, and the reason to
 * spend a sprite each here rather than tint a circle.
 *
 * The mark is the real mark, shrunk and lifted rather than redrawn: this
 * calls the member's own renderer under a transform, so the two silhouettes
 * cannot drift and the clock on the planned cone comes along for free.
 * ctx.scale takes the line widths with it, so halos stay in proportion.
 *
 * The plate is white with a dark ring, the same grammar the clock dial uses
 * and for its stated reason — a white shape on top of a white reflective band
 * needs the opposite colour between them or the two merge into one blob. It is
 * sized for three digits at every icon size the layer draws, which is what
 * lets the number be centred on it with one constant offset instead of a case
 * per digit count; see clusterTextOffsetEm() and its test.
 */
function onCountPlate(
  render: (ctx: CanvasRenderingContext2D, fill: string, outline: string) => void,
) {
  return (ctx: CanvasRenderingContext2D, fill: string, outline: string): void => {
    ctx.save();
    ctx.translate(0, COUNT_PLATE.markShift);
    ctx.scale(COUNT_PLATE.markScale, COUNT_PLATE.markScale);
    render(ctx, fill, outline);
    ctx.restore();

    const plate = () => {
      ctx.beginPath();
      ctx.roundRect(
        -COUNT_PLATE.width / 2, COUNT_PLATE.top,
        COUNT_PLATE.width, COUNT_PLATE.height, COUNT_PLATE.radius,
      );
    };

    ctx.lineJoin = 'round';
    ctx.shadowBlur = 0;
    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 8;
    plate(); ctx.stroke();

    ctx.fillStyle = '#ffffff';
    plate(); ctx.fill();

    ctx.strokeStyle = OUTLINE;
    ctx.lineWidth = 3.4;
    plate(); ctx.stroke();
  };
}

/**
 * A road hazard: the UK warning sign.
 *
 * White face, red border, black exclamation — drawn as the sign actually is,
 * because a driver has spent years learning to read it and copying it exactly
 * is free comprehension.
 *
 * The white face is also what keeps it apart from the flood and severe-weather
 * glyphs, which are solid amber triangles. A third solid triangle would have
 * been three layers sharing one silhouette; a white-faced, red-edged one is a
 * different object at a glance, which was the whole objection to a triangle
 * here in the first place.
 */
function hazardRender(withBridge: boolean) {
  return (ctx: CanvasRenderingContext2D, fill: string, _outline: string): void => {
    // The bridge variant draws ~12% larger. A bridge is a chokepoint whose
    // closure reroutes a region, and "bigger" was the ask — the size is the
    // first thing that reads, before the badge resolves.
    const k = withBridge ? 1.12 : 1;
    const tri = () => {
      ctx.beginPath();
      ctx.moveTo(0, -29 * k);
      ctx.lineTo(30 * k, 23 * k);
      ctx.lineTo(-30 * k, 23 * k);
      ctx.closePath();
    };

    ctx.lineJoin = 'round';

    // Dark separation first, so the red border does not bleed into a red
    // basemap feature or vanish against a dark one.
    ctx.strokeStyle = '#0b1016';
    ctx.lineWidth = 11;
    tri(); ctx.stroke();

    ctx.shadowBlur = 0;

    // The border is the fill of a large triangle; the face is a smaller one on
    // top. Simpler than stroking, and it keeps the corners genuinely rounded
    // rather than mitred.
    ctx.fillStyle = fill;
    tri(); ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(0, -17 * k);
    ctx.lineTo(21 * k, 16 * k);
    ctx.lineTo(-21 * k, 16 * k);
    ctx.closePath();
    ctx.fill();

    // The exclamation, weighted to survive being small.
    ctx.fillStyle = '#0b1016';
    ctx.beginPath();
    ctx.moveTo(-3.4 * k, -8 * k);
    ctx.lineTo(3.4 * k, -8 * k);
    ctx.lineTo(2.4 * k, 5 * k);
    ctx.lineTo(-2.4 * k, 5 * k);
    ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.arc(0, 10.5 * k, 3.1 * k, 0, Math.PI * 2);
    ctx.fill();

    if (!withBridge) return;

    // The badge, same grammar as the clock on the planned-works cone: a known
    // base plus a modifier in front, bottom-right, so a reader who has learned
    // "red triangle means incident" keeps that and gains "…on a bridge".
    const cx = 20;
    const cy = 21;
    const r = 12.5;
    const dial = () => {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
    };
    ctx.strokeStyle = '#0b1016';
    ctx.lineWidth = 8;
    dial(); ctx.stroke();
    ctx.fillStyle = '#ffffff';
    dial(); ctx.fill();
    ctx.strokeStyle = '#0b1016';
    ctx.lineWidth = 3;
    dial(); ctx.stroke();

    // The bridge: a deck over an arch, the simplest shape that says it.
    ctx.strokeStyle = '#0b1016';
    ctx.lineCap = 'round';
    ctx.lineWidth = 3.2;
    ctx.beginPath();
    ctx.moveTo(cx - 8, cy - 2.5); ctx.lineTo(cx + 8, cy - 2.5);
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy + 6.5, 6, Math.PI, 2 * Math.PI);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(cx - 8, cy - 2.5); ctx.lineTo(cx - 8, cy + 6.5);
    ctx.moveTo(cx + 8, cy - 2.5); ctx.lineTo(cx + 8, cy + 6.5);
    ctx.stroke();
  };
}

/**
 * A gantry message sign.
 *
 * Landscape and dark with amber lines across it, which is the silhouette of
 * every matrix sign on a British motorway. Distinct at a glance from the cone
 * and the hazard triangle: those are things beside the road, this is a thing
 * above it.
 *
 * The amber bars are the message, abstracted. Three of them because the signs
 * this reads are three rows — see maxNumberOfRows in the baked table — and the
 * middle bar is shortest, which is what a real two-or-three word message looks
 * like and stops it reading as a barcode.
 */
function vmsRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  _outline: string,
): void {
  const face = () => {
    ctx.beginPath();
    ctx.roundRect(-30, -20, 60, 36, 4);
  };
  const post = () => {
    ctx.beginPath();
    ctx.roundRect(-4, 14, 8, 16, 2);
  };

  ctx.lineJoin = 'round';

  // Pale halo under the whole thing, so a dark sign is visible on a dark
  // basemap — the same treatment every other sprite here gets.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.lineWidth = 8;
  face(); ctx.stroke();
  post(); ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  post(); ctx.fill();
  face(); ctx.fill();

  // The message. Amber on near-black, as the real thing is.
  ctx.fillStyle = '#ffb020';
  for (const [y, halfWidth] of [[-13, 21], [-2, 13], [9, 21]] as const) {
    ctx.beginPath();
    ctx.roundRect(-halfWidth, y, halfWidth * 2, 6, 3);
    ctx.fill();
  }
}

/**
 * A stopwatch, for journey times.
 *
 * Deliberately not the clock used on the planned-roadworks cone, and told
 * apart by two things a reader picks up without being told. That clock is
 * never alone — it is always leaning on a cone, and the cone is what is being
 * read. This one is a standalone object with a crown button and side lugs,
 * which is a stopwatch rather than a clock: a thing that measures elapsed
 * time, which is exactly what the number on the card is.
 *
 * Drawn in three colours — green, amber, red — because for this layer the
 * colour is the reading: "is the A90 moving" should be answerable before a
 * single number is read. The band comes from journeySprite(), which shares its
 * thresholds with the severity, so the marker and the card cannot disagree.
 *
 * It borrows the cone's amber and the hazard sign's red rather than inventing
 * a scale, so one severity language runs across every roads layer.
 */
function stopwatchRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  _outline: string,
): void {
  const dial = () => {
    ctx.beginPath();
    ctx.arc(0, 4, 24, 0, Math.PI * 2);
  };
  // The crown, and the two lugs either side of it. Small, but they are the
  // whole difference between "stopwatch" and "clock" at a glance.
  const crown = () => {
    ctx.beginPath();
    ctx.roundRect(-5, -30, 10, 10, 2);
  };
  const lugs = () => {
    ctx.beginPath();
    ctx.roundRect(-17, -25, 8, 7, 2);
    ctx.roundRect(9, -25, 8, 7, 2);
  };

  ctx.lineJoin = 'round';

  // Pale halo under the whole silhouette, as every sprite here gets.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.lineWidth = 8;
  dial(); ctx.stroke();
  crown(); ctx.stroke();
  lugs(); ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  crown(); ctx.fill();
  lugs(); ctx.fill();
  dial(); ctx.fill();

  // Face knocked out in the halo colour rather than white, so it reads as part
  // of the body at 20 px instead of as a separate mark.
  ctx.fillStyle = 'rgba(255, 255, 255, 0.94)';
  ctx.beginPath();
  ctx.arc(0, 4, 16.5, 0, Math.PI * 2);
  ctx.fill();

  // Hands at three o'clock: a right angle is what a dial reduces to when it is
  // small, and it survives the blur a naturalistic pose does not.
  ctx.strokeStyle = fill;
  ctx.lineCap = 'round';
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(0, 4); ctx.lineTo(0, -6);
  ctx.moveTo(0, 4); ctx.lineTo(8.5, 4);
  ctx.stroke();
}

/**
 * The no-entry disc: a road that is shut, not a road to take care on.
 *
 * The hazard triangle warns; this refuses. They are different messages on
 * every road sign chart in the country, and Traffic Scotland's own map draws
 * closures with exactly this disc — so a reader arriving from there keeps
 * the vocabulary they came with. Same dark separation ring as the triangle,
 * for the same reason: solid red on a red-tinged basemap disappears.
 */
function roadClosedRender(ctx: CanvasRenderingContext2D, fill: string, _outline: string): void {
  const disc = () => {
    ctx.beginPath();
    ctx.arc(0, 0, 24, 0, Math.PI * 2);
  };

  ctx.strokeStyle = '#0b1016';
  ctx.lineWidth = 9;
  disc(); ctx.stroke();

  ctx.shadowBlur = 0;

  ctx.fillStyle = fill;
  disc(); ctx.fill();

  // The bar, weighted like the sign's: wide enough to read as "no entry"
  // at map size rather than as a minus.
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  const w = 30;
  const h = 9;
  const r = h / 2;
  ctx.moveTo(-w / 2 + r, -h / 2);
  ctx.lineTo(w / 2 - r, -h / 2);
  ctx.arc(w / 2 - r, 0, r, -Math.PI / 2, Math.PI / 2);
  ctx.lineTo(-w / 2 + r, h / 2);
  ctx.arc(-w / 2 + r, 0, r, Math.PI / 2, -Math.PI / 2);
  ctx.closePath();
  ctx.fill();
}

function drawComposite(
  render: (ctx: CanvasRenderingContext2D, fill: string, outline: string) => void,
  fill: string,
): ImageData {
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d')!;
  ctx.translate(SIZE / 2, SIZE / 2);
  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  ctx.shadowColor = 'rgba(0, 0, 0, 0.65)';
  ctx.shadowBlur = 7;
  render(ctx, fill, OUTLINE);

  return ctx.getImageData(0, 0, SIZE, SIZE);
}

/**
 * An offshore installation: splayed legs, a deck, a derrick.
 *
 * Drawn nearly at the size of the box, with heavy strokes, because it has to be
 * identifiable at about twenty pixels. A first version used finer lines and a
 * cross-brace and read as a smudge — at this size a silhouette is the only
 * thing that survives, so the shape is three chunky ideas and nothing else.
 */
function rigRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  outline: string,
): void {
  /*
   * Ported from the supplied offshore-rig-marker.svg rather than shipped as
   * one, keeping this file's property that no icon costs a request or a binary
   * asset — the same treatment the helicopter got from its handoff SVG.
   *
   * The mapper carries the source's own 24×24 coordinates across rather than
   * hand-converted numbers, so the drawing can be checked against the SVG line
   * by line and a future revision is a re-port rather than a re-guess.
   *
   * ── Without the source's plate ─────────────────────────────────────────
   *
   * The SVG frames its glyph in a dark rounded square. Dropped here, and the
   * trade is worth writing down because it was measured rather than assumed:
   * the plate reads better at city zoom, where a white glyph on dark has
   * contrast a bare silhouette does not. Against that, it shares its outline
   * with the VMS sign, and it hides the one thing this drawing does better
   * than the shape it replaced — a recognisable platform profile with a flare.
   *
   * Rigs sit offshore with nothing else drawn near them, so a marker here has
   * no neighbours to be told apart from, and the small-size cost is one this
   * layer can afford in a way a road layer could not.
   */
  const S = 68 / 24;
  // Opened up from the source's 0.78: that scale is right inside a plate and
  // wrong without one, where the glyph itself has to fill the sprite.
  const G = S * 0.95;
  const g = (v: number) => (v - 12) * G;

  /** Derrick, flare boom, deck and legs — one path, so the halo is one outline. */
  const body = () => {
    ctx.beginPath();
    ctx.moveTo(g(8.35), g(4.6));
    ctx.lineTo(g(11.1), g(11.1));
    ctx.lineTo(g(5.6), g(11.1));
    ctx.closePath();
    ctx.moveTo(g(11.2), g(8.5));
    ctx.lineTo(g(16.1), g(5.65));
    ctx.lineTo(g(17.0), g(7.2));
    ctx.lineTo(g(12.1), g(10.05));
    ctx.closePath();
    ctx.roundRect(g(4.5), g(11.2), 11.2 * G, 2.4 * G, 0.4 * G);
    // Legs widened from the source's 1.8 units. At region zoom the drawn leg
    // is under a pixel at 1.8 and the halo simply swallows it, leaving a
    // platform apparently floating on nothing.
    ctx.rect(g(6.3), g(13.6), 2.2 * G, 4.5 * G);
    ctx.rect(g(11.5), g(13.6), 2.2 * G, 4.5 * G);
  };

  ctx.lineJoin = 'round';

  // Halo, then body — the pipeline every sprite here uses. Deliberately
  // lighter than the 13 the old rig used: this glyph has thinner members, and
  // a heavy outline closes the gaps between them into a blob.
  ctx.strokeStyle = outline;
  ctx.lineWidth = 8;
  body(); ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  body(); ctx.fill();

  // The flare, drawn last and ringed, so it stays the one warm mark rather
  // than merging into the boom it sits on. It is what says "producing
  // platform" instead of "structure at sea", and it is the thing the previous
  // sprite had no way of saying.
  const flare = () => {
    ctx.beginPath();
    ctx.arc(g(17.7), g(5.1), 2.3 * G, 0, Math.PI * 2);
  };
  ctx.strokeStyle = outline;
  ctx.lineWidth = 5;
  flare(); ctx.stroke();
  ctx.fillStyle = '#F0961F';
  flare(); ctx.fill();
}

/**
 * A wave buoy: a fat float with a short mast and a topmark.
 *
 * Round and squat against the rig's tall and angular, because the two sit on
 * the same layer and telling an instrument from an oil platform at a glance is
 * the whole reason they are different sprites. The float carries the
 * recognition, so it is the biggest element rather than a detail under a mast.
 */
/**
 * A combined sewer overflow monitor: a pipe discharging into water.
 *
 * From the design handoff (Overflow_icon_requirements, 20 Aug 2026), geometry
 * verbatim. Third member of the disc family beside the power bolt and the
 * river gauge, and it follows their documented rule: the disc's colour is the
 * state — the journey green/amber/red, one severity language across every
 * layer — and the glyph stays near-black on all of them, a hole punched in
 * the disc rather than a colour competing with it. Drawn, not an asset, like
 * everything else in this file; Scottish Water's own marks stay theirs.
 */
function csoOverflowRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  outline: string,
): void {
  const disc = () => { ctx.beginPath(); ctx.arc(0, 0, 32, 0, Math.PI * 2); };

  ctx.fillStyle = fill;
  disc(); ctx.fill();
  ctx.strokeStyle = outline;
  ctx.lineWidth = 1.6;
  disc(); ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.fillStyle = outline;
  ctx.beginPath();
  ctx.roundRect(-7, -28, 14, 20, 4);
  ctx.fill();
  ctx.beginPath();
  ctx.roundRect(-9, -10, 18, 6, 3);
  ctx.fill();

  ctx.strokeStyle = outline;
  ctx.lineWidth = 5;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(-20, 15);
  ctx.bezierCurveTo(-12, 5, -4, 25, 4, 15);
  ctx.bezierCurveTo(12, 5, 20, 25, 24, 15);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(1, -2, 3, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.arc(8, 4, 2.2, 0, Math.PI * 2);
  ctx.fill();
}

function buoyRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  outline: string,
): void {
  const float = () => {
    ctx.beginPath();
    ctx.ellipse(0, 12, 26, 20, 0, 0, Math.PI * 2);
  };
  const mast = () => {
    ctx.beginPath();
    ctx.moveTo(0, -4); ctx.lineTo(0, -30);
  };
  const topmark = () => {
    ctx.beginPath();
    ctx.arc(0, -34, 8, 0, Math.PI * 2);
  };

  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  ctx.strokeStyle = outline;
  ctx.lineWidth = 13;
  float(); ctx.stroke();
  mast(); ctx.stroke();
  topmark(); ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  float(); ctx.fill();
  ctx.strokeStyle = fill;
  ctx.lineWidth = 7;
  mast(); ctx.stroke();
  topmark(); ctx.fill();

  // A waterline stripe, which is what makes a filled ellipse read as a float
  // rather than as a dot. Dark rather than white so it works on both.
  ctx.strokeStyle = outline;
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(-22, 8); ctx.lineTo(22, 8);
  ctx.stroke();
}

/**
 * A lightning bolt, for the middle of a power circle.
 *
 * Sits on a coloured disc rather than on the map, so it is drawn dark with a
 * pale halo — the reverse of every other sprite here, which are pale shapes
 * haloed dark against a basemap. Getting that backwards makes it vanish on the
 * red ones.
 *
 * Registered as `power`, not `bolt`, though a bolt is what it draws. The name
 * is not decoration: the symbol layer resolves icon-image from the feature's
 * own `icon` field, so it has to match what the provider emits — and the
 * provider emits its own id. Naming it for the shape rather than the source is
 * how the first version of this drew nothing at all.
 */
function powerRender(
  ctx: CanvasRenderingContext2D,
  fill: string,
  _outline: string,
): void {
  const bolt = () => {
    ctx.beginPath();
    ctx.moveTo(6, -30);
    ctx.lineTo(-14, 4);
    ctx.lineTo(-1, 4);
    ctx.lineTo(-6, 30);
    ctx.lineTo(14, -6);
    ctx.lineTo(1, -6);
    ctx.closePath();
  };

  ctx.lineJoin = 'round';

  // Pale halo first, so the shape survives on a dark red disc.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.lineWidth = 9;
  bolt(); ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.fillStyle = fill;
  bolt(); ctx.fill();
}

/**
 * A river gauge: two waves, with an arrow saying which way the water is going.
 *
 * Same in-circle treatment as the bolt — dark on a pale halo, because it sits
 * on a coloured disc rather than on the basemap.
 *
 * The arrow is the whole point. A level is a number that means nothing to
 * somebody who does not know the river, but "coming up" is legible to anyone,
 * and it is the only part of a gauge reading that is actionable at a glance.
 * `null` draws the waves alone, for a station whose direction is not yet known
 * — one poll after a restart. No arrow is honest; a flat one would be a claim.
 */
function riverRender(direction: 'rising' | 'falling' | 'steady' | null) {
  return (ctx: CanvasRenderingContext2D, fill: string, _outline: string): void => {
    // One wave, not two, and it fills the width. A first attempt drew two
    // waves and a small arrow, which is the right picture on a card and the
    // wrong one at 15px: the strokes closed up into a single white blob. At
    // map sizes a glyph gets about two strokes' worth of detail, so the
    // budget goes on the wave and the arrow and nothing else.
    //
    // Without an arrow the wave sits centred; with one it drops to make room,
    // and between them they span the box the way the bolt does. A shape that
    // uses half its canvas draws at half the size the layer asked for.
    const waveY = direction ? 22 : 4;

    const wave = () => {
      ctx.beginPath();
      ctx.moveTo(-30, waveY);
      ctx.bezierCurveTo(-20, waveY - 14, -10, waveY + 14, 0, waveY);
      ctx.bezierCurveTo(10, waveY - 14, 20, waveY + 14, 30, waveY);
    };

    /** Which way the water is going, drawn above the water it describes. */
    const arrow = () => {
      ctx.beginPath();
      if (direction === 'steady') {
        // A level bar, deliberately not an arrow: steady is the absence of
        // movement, and a horizontal arrow would read as flow rather than as
        // a level holding.
        ctx.moveTo(-20, -14); ctx.lineTo(20, -14);
        return;
      }
      const up = direction === 'rising';
      const head = up ? -32 : 2;
      const tail = up ? 2 : -32;
      const barb = up ? head + 15 : head - 15;
      ctx.moveTo(0, tail); ctx.lineTo(0, head);
      ctx.moveTo(-13, barb); ctx.lineTo(0, head); ctx.lineTo(13, barb);
    };

    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    // Pale halo first, so the shape survives on a saturated disc — the same
    // reversal the bolt uses, and for the same reason.
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
    ctx.lineWidth = 17;
    wave(); ctx.stroke();
    if (direction) { arrow(); ctx.stroke(); }

    ctx.shadowBlur = 0;
    ctx.strokeStyle = fill;
    ctx.lineWidth = 9;
    wave(); ctx.stroke();
    if (direction) { arrow(); ctx.stroke(); }
  };
}

/**
 * An air quality monitoring station: three bands of haze.
 *
 * Deliberately the fog glyph rather than an instrument. What a reader wants
 * from this pin is "the air here", not "a Defra analyser cabinet here", and
 * the layer control already carries 🌫️ — a sprite that matches the control
 * icon costs nothing and saves a lookup.
 *
 * Three thin bands rather than the rivers' single wave, which is the one
 * thing it could be confused with: both are `environment`, both are made of
 * curves. At 15 px a single wave reads as one line and three bands read as a
 * texture, so they separate on density rather than on detail nobody can see.
 * The middle band is longest, so the shape has a centre of mass instead of
 * reading as a barcode.
 */
/**
 * The air quality pin, from the design handoff.
 *
 * A teardrop in the index's own colour, with the particulate glyph above the
 * number. The handoff builds it in CSS as a 72px square with
 * `border-radius: 50% 50% 50% 0` rotated -45°, which is the same shape as a
 * circle of radius 36 with two straight edges running out to a point. Working
 * that back to canvas geometry:
 *
 *   - the unrounded corner is the square's bottom-left, and -45° carries it to
 *     directly below the centre, at 36√2 ≈ 50.9 — that is the tip;
 *   - the rounding radius is half the side, so the curve begins at the
 *     midpoints of the two adjacent edges, which rotate to (±25.46, 25.46) —
 *     exactly 45° either side of straight down on the circle.
 *
 * So the outline is the major arc from 45° round the top to 135°, then two
 * straight lines to the tip. The design's own 36 is kept as the radius: at
 * that size the pin fits the 96px canvas with room for the shadow, and the
 * dot and number sizes transfer without rescaling.
 *
 * The pin is drawn with its tip near the bottom edge, because these are the
 * only sprites here anchored `bottom` rather than centred — a pin whose point
 * does not touch the thing it marks is worse than no pin.
 */
function airPinRender(index: number) {
  return (ctx: CanvasRenderingContext2D, fill: string, _outline: string): void => {
    const r = 36;
    // Tip near the bottom edge of the 96px canvas, body above it.
    const cy = -5;
    const tip = cy + r * Math.SQRT2;

    ctx.beginPath();
    // Anticlockwise from 45° to 135° is the LONG way round, over the top.
    ctx.arc(0, cy, r, Math.PI / 4, (Math.PI * 3) / 4, true);
    ctx.lineTo(0, tip);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();

    // Everything inside the pin sits on the fill, so the shadow is spent.
    ctx.shadowBlur = 0;

    // Particulate glyph: three dots, 5/8/5, bottom-aligned, 3px apart. The
    // content block is 30 tall (8 dots + 2 gap + 20 number) and centred in
    // the circle, so it reads as one mark rather than two.
    const top = cy - 15;
    const dotsBottom = top + 8;
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    for (const [x, d] of [[-9.5, 5], [0, 8], [9.5, 5]] as const) {
      ctx.beginPath();
      ctx.arc(x, dotsBottom - d / 2, d / 2, 0, Math.PI * 2);
      ctx.fill();
    }

    // The number, in the handoff's own face and weight.
    ctx.fillStyle = '#000000';
    ctx.font = '800 20px Arial, Helvetica, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(String(index), 0, cy + 5);
  };
}

function airRender(ctx: CanvasRenderingContext2D, fill: string, _outline: string): void {
  const band = (y: number, halfWidth: number) => {
    ctx.beginPath();
    ctx.moveTo(-halfWidth, y);
    ctx.bezierCurveTo(-halfWidth / 2, y - 7, halfWidth / 2, y + 7, halfWidth, y);
  };
  const bands = () => {
    band(-17, 20);
    band(0, 28);
    band(17, 20);
  };

  ctx.lineJoin = 'round';
  ctx.lineCap = 'round';

  // Pale halo first, so the shape survives on a saturated disc — the same
  // reversal the bolt and the river wave use, and for the same reason.
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.92)';
  ctx.lineWidth = 13;
  bands(); ctx.stroke();

  ctx.shadowBlur = 0;
  ctx.strokeStyle = fill;
  ctx.lineWidth = 7;
  bands(); ctx.stroke();
}

const OUTLINE = '#0b1016';

/**
 * Every sprite the map can draw, by name.
 *
 * `Record<string, ImageData>` rather than `Record<IconName, ImageData>` since
 * the festival stars arrived: there is one per festival id, generated from
 * FESTIVAL_COLOURS rather than written out, so the key set is no longer a
 * union known at compile time. Callers index it with a feature's `icon`, which
 * was always a plain string off the wire.
 */
/**
 * The chevron repeated along a route line to say which way the bus goes.
 *
 * Points along +x, which is the direction MapLibre lays a symbol out in when
 * `symbol-placement` is `line`: the arrow inherits the line's own coordinate
 * order, and that order is the direction of travel all the way back to
 * shapes.txt. So the sprite never has to be told which way to face.
 *
 * Notched at the tail rather than a plain triangle. A triangle at this size
 * reads as a dot; the notch is what survives it, the same reasoning as the rig
 * sprite's silhouette above.
 *
 * The numbers come from map-style.ts rather than living here, because the
 * shape and the icon-size curve are one decision: this was first drawn at ±15
 * in a canvas whose convention is ±34, sized as though it filled that canvas,
 * and shipped arrows about five pixels tall on an eight-pixel line. Deriving
 * the drawn size from the same constants the curve uses is what stops the two
 * disagreeing again.
 */
function routeArrowPath(ctx: CanvasRenderingContext2D): void {
  ctx.moveTo(ROUTE_ARROW.back, -ROUTE_ARROW.flank);
  ctx.lineTo(ROUTE_ARROW.tip, 0);
  ctx.lineTo(ROUTE_ARROW.back, ROUTE_ARROW.flank);
  ctx.lineTo(ROUTE_ARROW.notch, 0);
}

/**
 * Every sprite a cluster can be drawn from, with what it is drawn in.
 *
 * The plain sprite and its cluster sprite both come from this one entry, so
 * the two drawings cannot drift — the same argument the festival stars and
 * the air quality pins make below against near-identical entries in three
 * files, and the argument REPLACES_CIRCLE's history makes twice about lists
 * somebody has to remember.
 *
 * Only the clusterable ones live here. A sprite that never collapses has
 * nothing to keep in step and stays where it always was.
 */
const CLUSTERABLE: Record<string, {
  render: (ctx: CanvasRenderingContext2D, fill: string, outline: string) => void;
  fill: string;
}> = {
  roadworks: { render: coneRender(false), fill: PALETTE.cone },
  // Same cone, same orange: planned works are these works, later. The clock
  // is the only difference because "later" is the only difference.
  'roadworks-future': { render: coneRender(true), fill: PALETTE.cone },
  // The CSO states: same render, the disc colour is the message. The three
  // real states are the journey severities by reference, so the layers can
  // never drift apart in a future colour change.
  'cso-not-discharging': { render: csoOverflowRender, fill: PALETTE.journey },
  'cso-recent': { render: csoOverflowRender, fill: PALETTE.journeySlow },
  'cso-discharging': { render: csoOverflowRender, fill: PALETTE.journeyStopped },
  'cso-no-data': { render: csoOverflowRender, fill: PALETTE.csoNoData },
};

export function buildIcons(): Record<string, ImageData> {
  return {
    /*
     * One star per festival, same shape, thirteen colours.
     *
     * Generated rather than listed, so adding a festival is a line in
     * FESTIVAL_COLOURS and nothing else — the alternative is thirteen
     * near-identical entries here, thirteen in REPLACES_CIRCLE and thirteen in
     * layer-icons.ts, which is three places to forget.
     */
    ...Object.fromEntries(
      Object.entries(FESTIVAL_COLOURS).map(([id, colour]) =>
        [festivalIconName(id), drawComposite(fringeRender, colour)]),
    ),
    plane: draw(planePath, PALETTE.plane),
    helicopter: drawComposite(helicopterRender, PALETTE.helicopter),
    cargo: draw(cargoPath, PALETTE.ship, cargoOverlay),
    tanker: draw(tankerPath, PALETTE.ship, tankerOverlay),
    fishing: draw(fishingPath, PALETTE.ship, fishingOverlay),
    ship: draw(plainBoatPath, PALETTE.ship),
    // Passenger shape for both, but ferries keep the ferry colour so the
    // NorthLink sailings stay identifiable — matches the existing tokens and
    // the legend swatch.
    passenger: draw(passengerPath, PALETTE.ship, passengerOverlay),
    ferry: draw(passengerPath, PALETTE.ferry, passengerOverlay),
    bus: draw(busPath, PALETTE.bus),
    fuel: drawComposite(fuelPumpRender, PALETTE.fuel),
    rig: drawComposite(rigRender, PALETTE.rig),
    buoy: drawComposite(buoyRender, PALETTE.buoy),
    power: drawComposite(powerRender, PALETTE.power),
    'air-station': drawComposite(airRender, PALETTE.air),
    /*
     * The index pins, one per DAQI value, coloured from the official ramp.
     *
     * Generated from DAQI_COLOURS for the same reason the festival stars are
     * generated: the alternative is ten near-identical entries here and ten
     * more in REPLACES_CIRCLE, which is two places to forget one.
     */
    ...Object.fromEntries(DAQI_COLOURS.map(
      (colour, i) => [airIconName(i + 1), drawComposite(airPinRender(i + 1), colour)],
    )),
    river: drawComposite(riverRender(null), PALETTE.river),
    'river-rising': drawComposite(riverRender('rising'), PALETTE.river),
    'river-falling': drawComposite(riverRender('falling'), PALETTE.river),
    'river-steady': drawComposite(riverRender('steady'), PALETTE.river),
    // Deliberately the same drawing as the river gauges. A rising river and a
    // rising tide are the same picture because they are the same idea — water
    // going up — and inventing a second visual language for it would be
    // decoration rather than information. Where they are on the map does the
    // disambiguating: gauges sit inland on the Dee and the Don, tides sit in
    // the harbours.
    'tide-rising': drawComposite(riverRender('rising'), PALETTE.tide),
    'tide-falling': drawComposite(riverRender('falling'), PALETTE.tide),
    // Slack water, at the turn: neither in nor out, so a level bar rather than
    // an arrow that would claim a direction the sea is not going in.
    'tide-flat': drawComposite(riverRender('steady'), PALETTE.tide),
    taxi: drawComposite(taxiRender, PALETTE.taxi),
    camera: drawComposite(cameraRender, PALETTE.camera),
    // Tram platforms. Not a provider's sprite — the only entry here asked for by
    // the frontend's own baked-stop layer rather than by a feature's `icon`
    // field, which is why the layer names it as a literal in map.ts.
    'tram-stop': drawComposite(tramStopRender, PALETTE.tramStop),
    // Subway stations: the other baked-stop sprite, named as a literal by its
    // layer in map.ts like the tram platform is. There is no ferry-stop
    // sprite — the terminal is the ⚓ Ferries pin, sailings and all.
    'subway-stop': drawComposite(subwayStopRender, PALETTE.subwayStop),
    /*
     * Everything a cluster can be made of, drawn twice: as itself, and on a
     * plate for a count. Generated from CLUSTERABLE so a state added there
     * arrives on the map both ways at once — the roadworks cones, the four
     * sewer overflow states, and whatever #8 and #9 bring.
     */
    ...Object.fromEntries(Object.entries(CLUSTERABLE).map(
      ([name, { render, fill }]) => [name, drawComposite(render, fill)],
    )),
    /*
     * And their cluster marks, driven off CLUSTERED_ICONS rather than off the
     * table, so the two lists are checked against each other here rather than
     * by somebody noticing.
     *
     * A name with no drawing behind it is not a reference error: MapLibre logs
     * one line about a missing image and draws no icon at all, leaving a bare
     * number floating on the map. Throwing at boot is the loud version of that
     * — the map fails to build rather than quietly drawing half a mark.
     */
    ...Object.fromEntries(CLUSTERED_ICONS.map((icon) => {
      const base = CLUSTERABLE[icon];
      if (!base) throw new Error(`${icon} clusters but has no drawing in CLUSTERABLE`);
      return [clusterSpriteFor(icon), drawComposite(onCountPlate(base.render), base.fill)];
    })),
    incident: drawComposite(hazardRender(false), PALETTE.hazard),
    // The same sign, larger and badged — an incident on one of the twelve
    // managed bridges, where a closure reroutes a region.
    'incident-bridge': drawComposite(hazardRender(true), PALETTE.hazard),
    // The no-entry disc, for records whose type says the road is shut.
    'road-closed': drawComposite(roadClosedRender, PALETTE.hazard),
    vms: drawComposite(vmsRender, PALETTE.vms),
    // Three of the same watch. A canvas sprite is not SDF, so the style cannot
    // recolour one at draw time — the colour has to be baked in, which means an
    // image per band. Three small canvases cost nothing next to a request.
    journey: drawComposite(stopwatchRender, PALETTE.journey),
    'journey-slow': drawComposite(stopwatchRender, PALETTE.journeySlow),
    'journey-stopped': drawComposite(stopwatchRender, PALETTE.journeyStopped),
    // One image per formation length: MapLibre picks by icon-image, so the
    // count has to be baked into the sprite rather than computed at draw time.
    // Nine tiny canvases is nothing next to a single extra network request.
    ...trainIcons(),
    /*
     * Direction of travel on a drawn route (issue #40).
     *
     * Dark-bodied on purpose, unlike every sprite above it. Those sit on the
     * basemap and need to carry their own colour; this one sits *on* the
     * route line, which is already the layer's yellow, so it reads as a notch
     * cut into the line rather than as another object competing with it. The
     * light rim draw() adds is what keeps it legible where the line is thin.
     *
     * Named as a literal by its layer in map.ts, like the tram and Subway
     * platforms — no provider asks for this one.
     */
    'route-arrow': draw(routeArrowPath, '#0b1016'),
  };
}

/**
 * train1 … train8. Built as a loop and asserted back to the union rather than
 * written out eight times: Object.fromEntries widens the keys to string, which
 * TypeScript cannot narrow back to the template-literal type on its own.
 */
function trainIcons(): Record<`train${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}`, ImageData> {
  const out = {} as Record<string, ImageData>;
  for (let n = 1; n <= MAX_DRAWN_CARRIAGES; n += 1) {
    out[`train${n}`] = draw(trainPathFor(n), PALETTE.train, trainOverlayFor(n));
  }
  return out as Record<`train${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8}`, ImageData>;
}
